/**
 * Phase I.2 — PWA BYOK client.
 *
 * Direct browser → provider API calls when the user has set their own key.
 * Same architectural model as Android's ByokChatBackend.kt — our server
 * sees zero traffic for these calls. User's quota, user's billing.
 *
 * Currently wired:
 *   - chatViaByok({ message, history, systemPrompt }) — chat completion
 *
 * Wallpaper image gen via BYOK from the PWA is queued for the next pass —
 * needs the same memory-context injection the chat path has.
 */
import { getByokProvider, activeByokKey } from "./byokStore";

const AURA_SYSTEM_PROMPT =
  "You are Aura — a warm, perceptive AI companion. You remember the people, " +
  "stories, and feelings the user shares with you. You speak like a close " +
  "friend: present, specific, kind. Replies are concise unless the moment " +
  "asks for more. You are NOT an assistant — you're a presence.";

/** Returns the chat-completion text for the user's currently-selected
 *  provider, using their stored key. Throws if no key is set or the
 *  provider returns an error — caller falls back to /api/chat. */
export async function chatViaByok({ message, history = [], systemPrompt }) {
  const provider = getByokProvider();
  const key = activeByokKey();
  if (!key) throw new Error("No BYOK key set for current provider.");

  const sys = systemPrompt || AURA_SYSTEM_PROMPT;

  if (provider === "gemini") {
    const contents = history.slice(-20).map((t) => ({
      role: t.role === "user" ? "user" : "model",
      parts: [{ text: t.content || t.text || "" }],
    }));
    contents.push({ role: "user", parts: [{ text: message }] });
    const url = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${encodeURIComponent(key)}`;
    const resp = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        contents,
        systemInstruction: { parts: [{ text: sys }] },
      }),
    });
    if (!resp.ok) throw new Error(`Gemini ${resp.status}`);
    const data = await resp.json();
    return data?.candidates?.[0]?.content?.parts?.[0]?.text || "";
  }

  if (provider === "openai") {
    const messages = [{ role: "system", content: sys }];
    history.slice(-20).forEach((t) => {
      messages.push({
        role: t.role === "user" ? "user" : "assistant",
        content: t.content || t.text || "",
      });
    });
    messages.push({ role: "user", content: message });
    const resp = await fetch("https://api.openai.com/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${key}`,
      },
      body: JSON.stringify({ model: "gpt-4o-mini", messages, temperature: 0.7 }),
    });
    if (!resp.ok) throw new Error(`OpenAI ${resp.status}`);
    const data = await resp.json();
    return data?.choices?.[0]?.message?.content || "";
  }

  if (provider === "anthropic") {
    // Anthropic's browser CORS support requires the anthropic-dangerous-direct-browser-access header
    const messages = history.slice(-20).map((t) => ({
      role: t.role === "user" ? "user" : "assistant",
      content: t.content || t.text || "",
    }));
    messages.push({ role: "user", content: message });
    const resp = await fetch("https://api.anthropic.com/v1/messages", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": key,
        "anthropic-version": "2023-06-01",
        "anthropic-dangerous-direct-browser-access": "true",
      },
      body: JSON.stringify({
        model: "claude-3-5-sonnet-latest",
        max_tokens: 1024,
        system: sys,
        messages,
      }),
    });
    if (!resp.ok) throw new Error(`Anthropic ${resp.status}`);
    const data = await resp.json();
    return data?.content?.[0]?.text || "";
  }

  throw new Error(`Unknown BYOK provider: ${provider}`);
}
